import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { GoogleAuthProvider } from '@/modules/auth/components/GoogleAuthProvider';

interface AuthLayoutLink {
  prompt: string;
  action: string;
  to: string;
}

interface AuthLayoutProps {
  /** Shown above the heading, e.g. the sign-in provider's logo on its callback. */
  mark?: ReactNode;
  heading: string;
  subheading: ReactNode;
  /** "New to Finlay? Create an account" style switch between login and sign-up. */
  switchLink?: AuthLayoutLink;
  children?: ReactNode;
}

/**
 * Portal-style chrome for `/login` and the LinkedIn and Slack callbacks: a
 * hairline card on the portal's sidebar canvas (`#fafafa`), logo above, the
 * optional login/sign-up switch below. The card sits at a fixed top offset so
 * the heading is in the same place on every auth page. Wraps the Google OAuth
 * provider the SSO button needs.
 */
export const AuthLayout = ({
  mark,
  heading,
  subheading,
  switchLink,
  children,
}: AuthLayoutProps) => (
  <GoogleAuthProvider>
    <div className="portal text-foreground flex min-h-svh flex-col items-center bg-[#fafafa] px-4 py-10 md:py-16">
      <img src="/logo.png" alt="Finlay" className="h-7 w-auto object-contain" />

      <div className="mt-8 w-full max-w-[400px]">
        <div className="bg-background rounded-lg border p-6 shadow-xs md:p-8">
          {mark}
          <h1 className="text-[22px] leading-7 font-semibold tracking-[-0.02em] text-balance">
            {heading}
          </h1>
          <div className="text-muted-foreground mt-1 text-[13px]">
            {subheading}
          </div>

          {children && <div className="mt-6 space-y-5">{children}</div>}
        </div>

        {switchLink && (
          <p className="text-muted-foreground mt-6 text-center text-[13px]">
            {switchLink.prompt}{' '}
            <Link
              to={switchLink.to}
              className="text-primary-700 hover:text-primary focus-visible:ring-ring/50 rounded-sm font-medium underline-offset-4 outline-none hover:underline focus-visible:ring-[3px]"
            >
              {switchLink.action}
            </Link>
          </p>
        )}
      </div>
    </div>
  </GoogleAuthProvider>
);

/** "or" rule between the email form and the SSO buttons. */
export const OrDivider = () => (
  <div className="flex items-center gap-3" aria-hidden="true">
    <span className="bg-border h-px flex-1" />
    <span className="text-muted-foreground text-xs">or</span>
    <span className="bg-border h-px flex-1" />
  </div>
);
